import React from "react";
import { QUADRANT_COLORS } from "../utils/calendarUtils";

const DailyTasksView = ({ 
  selectedDate, 
  view, 
  setView, 
  appointments, 
  onAddNewEvent, 
  onAppointmentClick,
  onToggleTaskCompletion 
}) => {
  // Convert appointments to tasks for the selected date
  const tasksForSelectedDate = appointments
    .filter(apt => {
      if (!apt.dateTime) return false;
      const aptDate = new Date(apt.dateTime);
      const selected = new Date(selectedDate);
      return (
        aptDate.getDate() === selected.getDate() &&
        aptDate.getMonth() === selected.getMonth() &&
        aptDate.getFullYear() === selected.getFullYear()
      );
    })
    .map(apt => ({
      id: apt.id,
      title: apt.title,
      quadrant: apt.quadrant || "Not Specified",
      type: "appointment",
      completed: apt.completed || false,  // Make sure we use the completed status
      dateTime: apt.dateTime,
      time: apt.dateTime ? apt.dateTime.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      }) : ""
    }));

  const getQuadrantColor = (quadrant) => {
    return QUADRANT_COLORS[quadrant]?.color || "#6b7280";
  };

  const handleToggleCompletion = async (taskId, currentStatus) => {
    if (onToggleTaskCompletion) {
      try {
        await onToggleTaskCompletion(taskId);
        console.log(`Task ${taskId} completion toggled from ${currentStatus} to ${!currentStatus}`);
      } catch (error) {
        console.error('Error toggling task completion:', error);
        alert('Error updating task status: ' + error.message);
      }
    }
  };

  return (
    <div className="daily-tasks-view">
      <div className="view-header">
        <div className="view-title">My Daily Tasks</div>
        <div className="view-actions">
          <button 
            className="add-event-btn"
            onClick={onAddNewEvent}
          >
            Add New Event
          </button>
          <div className="view-selector">
            <button 
              className={view === "day" ? "view-btn-calendar active" : "view-btn-calendar"}
              onClick={() => setView("day")}
            >
              Day
            </button>
            <button 
              className={view === "8days" ? "view-btn-calendar active" : "view-btn-calendar"}
              onClick={() => setView("8days")}
            >
              8 days
            </button>
          </div>
        </div>
      </div>

      {/* Quick Add Section */}
      <div className="quick-add-section">
        <h3>Add New Task/Event</h3>
        <p className="quick-add-note">
          Click "Add New Event" to create appointments that will appear here as tasks
        </p>
      </div>

      {/* Tasks List */}
      <div className="tasks-list-section">
        <div className="tasks-header">
          <h3>Tasks & Appointments for {selectedDate.toLocaleDateString()}</h3>
          <div className="tasks-count">
            {tasksForSelectedDate.length} items
          </div>
        </div>

        {tasksForSelectedDate.length === 0 ? (
          <div className="no-tasks">
            <p>No tasks or appointments for this date.</p>
            <p>Click "Add New Event" to create your first appointment!</p>
          </div>
        ) : (
          <div className="tasks-table">
            <div className="table-header">
              <div className="col-status">Status</div>
              <div className="col-title">Task/Appointment</div>
              <div className="col-time">Time</div>
              <div className="col-quadrant">Quadrant</div>
              <div className="col-type">Type</div>
              <div className="col-actions">Actions</div>
            </div>
            
            <div className="table-body">
              {tasksForSelectedDate.map(task => (
                <div key={task.id} className={`task-row ${task.completed ? 'completed' : ''}`}>
                  <div className="col-status">
                    <input
                      type="checkbox"
                      checked={task.completed}
                      onChange={() => handleToggleCompletion(task.id, task.completed)}
                      className="task-checkbox"
                    />
                  </div>
                  <div className="col-title">
                    <span 
                      className="task-title"
                      onClick={() => onAppointmentClick(task)}
                      style={{cursor: 'pointer'}}
                    >
                      {task.title}
                    </span>
                  </div>
                  <div className="col-time">
                    <span className="task-time">{task.time}</span>
                  </div>
                  <div className="col-quadrant">
                    <span 
                      className="quadrant-badge"
                      style={{ backgroundColor: getQuadrantColor(task.quadrant) }}
                    >
                      {task.quadrant}
                    </span>
                  </div>
                  <div className="col-type">
                    <span className="type-badge appointment">
                      {task.type === "appointment" ? "Appointment" : "Task"}
                    </span>
                  </div>
                  <div className="col-actions">
                    <button
                      onClick={() => onAppointmentClick(task)}
                      className="edit-btn"
                      title="Edit appointment"
                    >
                      ✏️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="tasks-stats">
        <div className="stat">
          <span className="stat-label">
            {tasksForSelectedDate.filter(t => t.completed).length}
          </span>
          <span className="stat-label"> Completed</span>
        </div>
        <div className="stat">
          <span className="stat-label">
            {tasksForSelectedDate.filter(t => !t.completed).length}
          </span>
          <span className="stat-label"> Pending</span>
        </div>
        <div className="stat">
          <span className="stat-label">
            {tasksForSelectedDate.length}
          </span>
          <span className="stat-label"> Total</span>
        </div>
      </div>
    </div>
  );
};

export default DailyTasksView;